import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Test if browser WebRTC is fully compatible with Telnyx
 * Returns true if WebRTC is likely to work
 */
const testWebRTCCompatibility = () => {
  try {
    // Check if RTCPeerConnection exists
    if (!window.RTCPeerConnection) {
      return false;
    }
    
    // Create a test peer connection
    const pc = new RTCPeerConnection();
    
    // Check if getTransceivers is supported
    if (typeof pc.getTransceivers !== 'function') {
      pc.close();
      return false;
    }
    
    // Try to actually call getTransceivers
    try {
      pc.getTransceivers();
    } catch (e) {
      pc.close();
      return false;
    }
    
    // Check if addTransceiver is supported (required by Telnyx)
    if (typeof pc.addTransceiver !== 'function') {
      pc.close();
      return false;
    }
    
    pc.close();
    return true;
  } catch (e) {
    console.log('WebRTC compatibility test failed:', e);
    return false;
  }
};

/**
 * Custom hook for Telnyx WebRTC calling and status
 */
export function useCrmTelnyx(leadId, lead, setCallForm, setCallDialogOpen) {
  const [telnyxStatus, setTelnyxStatus] = useState({ 
    configured: false, 
    phone_number: null, 
    webrtc_enabled: false 
  });
  const [callingNumber, setCallingNumber] = useState(null);
  const [webrtcCallOpen, setWebrtcCallOpen] = useState(false);
  const [webrtcCallNumber, setWebrtcCallNumber] = useState(null);
  const [callInProgress, setCallInProgress] = useState(false);
  const [webrtcFailed, setWebrtcFailed] = useState(false);
  const webrtcCompatibleRef = useRef(null); // Cache compatibility check

  // Fetch Telnyx status
  const fetchTelnyxStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/telnyx/status`);
      setTelnyxStatus(res.data);
    } catch {
      console.log('Telnyx not configured');
    }
  }, []);

  useEffect(() => {
    fetchTelnyxStatus();
    // Run WebRTC compatibility check once on mount
    if (webrtcCompatibleRef.current === null) {
      webrtcCompatibleRef.current = testWebRTCCompatibility();
      console.log('WebRTC compatibility:', webrtcCompatibleRef.current);
    }
  }, [fetchTelnyxStatus]);

  // Fallback to server-side calling
  const fallbackToServerCall = useCallback(async (phoneNumber) => {
    setCallingNumber(phoneNumber);
    try {
      await axios.post(`${API}/telnyx/call`, {
        to_number: phoneNumber,
        lead_id: leadId
      });
      toast.success(`Calling ${phoneNumber}...`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setTimeout(() => setCallingNumber(null), 3000);
    }
  }, [leadId]);

  // Handle WebRTC error - fallback to server-side calling
  // webrtcFailed resets after 60s so user can retry WebRTC
  const handleWebRTCError = useCallback((error, phoneNumber) => {
    console.log('WebRTC error, falling back to server-side call:', error);
    setWebrtcFailed(true);
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
    toast.info('Switching to phone call mode...');
    // Reset webrtcFailed after 60s so next call attempt can try WebRTC again
    setTimeout(() => setWebrtcFailed(false), 60000);
    // Fall back to server-side call
    if (phoneNumber) {
      fallbackToServerCall(phoneNumber);
    }
  }, [fallbackToServerCall]);

  // Click-to-call function - tries WebRTC first if compatible, falls back to server-side
  const handleClickToCall = async (phoneNumber) => {
    if (!telnyxStatus.configured) {
      window.location.href = `tel:${phoneNumber}`;
      return;
    }

    // Check if WebRTC is available and compatible
    const canUseWebRTC = telnyxStatus.webrtc_enabled && webrtcCompatibleRef.current === true;

    console.log('handleClickToCall:', {
      phoneNumber,
      webrtc_enabled: telnyxStatus.webrtc_enabled,
      webrtcFailed,
      webrtcCompatible: webrtcCompatibleRef.current,
      canUseWebRTC,
    });

    if (!canUseWebRTC) {
      // Use server-side calling directly
      console.log('Using server-side calling (WebRTC not available)');
      fallbackToServerCall(phoneNumber);
      return;
    }

    // Always try WebRTC — reset webrtcFailed on each new attempt
    setWebrtcFailed(false);
    setWebrtcCallNumber(phoneNumber);
    setWebrtcCallOpen(true);
    // Close LogCallDialog if open — avoid two windows
    setCallDialogOpen(false);
  };

  // Handle WebRTC call connected — prepare the call form but don't open dialog yet
  const handleWebRTCCallConnected = (dialedNumber) => {
    setCallForm(f => ({
      ...f,
      phone_number: dialedNumber || webrtcCallNumber || f.phone_number,
      notes: '',
      call_outcome: 'connected',
      duration_seconds: 0
    }));
    // Don't open CallLogDialog now — it overlays the call widget.
    // It will open when the call ends (handleWebRTCCallEnded).
  };

  // Handle WebRTC call ended — recordingId is passed after upload completes
  const handleWebRTCCallEnded = (duration, recordingId) => {
    setCallForm(f => ({
      ...f,
      duration_seconds: duration,
      ...(recordingId ? { recording_id: recordingId } : {}),
    }));
    setWebrtcCallOpen(false);
    setWebrtcCallNumber(null);
    // Now open the call log dialog so user can log the call
    setCallDialogOpen(true);
  };

  // Handle duration updates during call
  const handleDurationUpdate = (duration) => {
    setCallForm(f => ({ ...f, duration_seconds: duration }));
  };

  // Make call via Telnyx server-side
  const handleMakeCall = async (phoneNumber) => {
    if (!phoneNumber) {
      toast.error('Please select a phone number');
      return;
    }
    
    setCallInProgress(true);
    try {
      await axios.post(`${API}/telnyx/call`, {
        to_number: phoneNumber,
        lead_id: leadId
      });
      toast.success(`Call initiated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
      setCallInProgress(false);
    }
  };

  return {
    telnyxStatus,
    callingNumber,
    webrtcCallOpen,
    setWebrtcCallOpen,
    webrtcCallNumber,
    setWebrtcCallNumber,
    callInProgress,
    setCallInProgress,
    handleClickToCall,
    handleWebRTCCallConnected,
    handleWebRTCCallEnded,
    handleDurationUpdate,
    handleMakeCall,
    handleWebRTCError,
  };
}

export default useCrmTelnyx;
